<template>
  <div>
    <AlertLayout title="Reminders!"
      message="Kindly make sure you have stable internet and power before starting the test" type="warning" v-if="!readStatusLoading && !alreadyRead" />

    <div class="flex flex-col items-center justify-center">
      <div class="rounded-lg w-full p-6">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4" v-if="!readStatusLoading">
          <div>
            <p class="text-xs uppercase tracking-wide text-gray-500">Action</p>
            <p class="text-sm text-gray-600">By confirming, you acknowledge you have read and understood this material.</p>
          </div>
          <div class="flex items-center gap-2">
            <button
              class="px-4 py-2 rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed"
              :class="alreadyRead ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'"
              :disabled="isMarkingRead || alreadyRead"
              @click="onMarkAsRead"
            >
              <span v-if="isMarkingRead">Marking…</span>
              <span v-else-if="alreadyRead">Already marked as read</span>
              <span v-else>Mark as read</span>
            </button>
            <span v-if="alreadyRead && document.read_at" class="text-sm text-gray-600">Read on {{ readOn }} <span class="text-gray-400">· {{ readOnRelative }}</span></span>
          </div>
        </div>
        <div v-else class="mb-4 text-sm text-gray-500">Checking read status…</div>
        <div class="grid grid-cols-2 gap-4">
          <div class="border-b-2">
            <p class="font-bold py-1">Document Name:</p>
            <p class="py-2">{{ document.document_name }}</p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Document Type:</p>
            <p class="py-2">{{ document.document_type }}</p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Deadline:</p>
            <p class="py-2">{{ new Date(document.deadline).toLocaleString() }}</p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Author:</p>
            <p class="py-2">{{ document.created_by?.first_name }} {{ document.created_by?.last_name }}</p>
          </div>
        </div>
        <div class="grid grid-cols-1 gap-4">
          <div class="border-b-2">
            <p class="font-bold py-1">Description:</p>
            <p class="py-2">{{ document.description }}</p>
          </div>
        </div>
        <div class="mt-4">
          <p class="font-bold py-1">Uploaded Document:</p>
          <div class="py-2 px-2">
            <iframe :src="`${document.file_url}`" width="100%" height="700px"></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AlertLayout from '@/components/AlertLayout.vue';
import api from '@/services/api';
import { SweetAlertService } from '@/services/SweetAlertService';

export default {
  name: "StaffAssessmentDetailsPage",
  components: {
    AlertLayout
  },
  data() {
    return {
      document: [],
      document_id: null,
      isMarkingRead: false,
      readStatusLoading: true,
    }
  },
  created() {
    this.document_id = this.$route.params.document_id;
    this.initLoad();
  },
  watch: {
    currentUserId(newVal) {
      if (newVal && !this.document?.read_at) {
        this.fetchReaders();
      }
    }
  },
  methods: {
    user() {
      return this.$store.getters["auth/user"];
    },

    async initLoad() {
      try {
        await this.fetchDocument();
        await this.fetchReaders();
      } finally {
        this.readStatusLoading = false;
      }
    },

    async fetchDocument() {
      try {
        const response = await api.getDocument(this.document_id);
        const incoming = response.data || response;
        // Merge to preserve any locally known read state
        this.document = { ...(this.document || {}), ...(incoming || {}) };
      } catch (error) {
        this.error = error.message || "Failed to fetch documents";
      }
    },

    async fetchReaders() {
      try {
        const res = await api.getDocumentReadersAll(this.document_id);
        const readers = res?.data || res || [];
        const meId = this.currentUserId;
        const meEmail = this.currentUserEmail;
        const mine = Array.isArray(readers) ? readers.find(r => r.user_id === meId || r.email === meEmail) : null;
        if (mine?.read_at) {
          this.document = { ...(this.document || {}), read_at: mine.read_at, is_read: true, read: true };
        }
      } catch (e) {
        // ignore readers fetch errors
      }
    },

    async onMarkAsRead() {
      const confirm = await SweetAlertService.showConfirmation(
        'Confirm you have read this document',
        'By continuing, you acknowledge that you have carefully read and understood the contents of this document.'
      );
      if (!confirm.isConfirmed) return;
      this.isMarkingRead = true;
      try {
        const res = await api.markDocumentRead(this.document_id);
        // Use server-provided timestamp for immediate UI feedback
        const readAt = res?.read_at || new Date().toISOString();
        this.document = { ...(this.document || {}), read_at: readAt, is_read: true, read: true };
        // Refresh from server; if server payload lacks read status, preserve local
        const prevReadAt = readAt;
        await this.fetchDocument();
        await this.fetchReaders();
        if (!this.document?.read_at) {
          this.document = { ...(this.document || {}), read_at: prevReadAt, is_read: true, read: true };
        }
        SweetAlertService.showSuccess('Marked as read', 'Thank you. Your acknowledgement has been recorded.');
      } catch (error) {
        SweetAlertService.showError('Unable to mark as read', error.message || 'Please try again.');
      } finally {
        this.isMarkingRead = false;
      }
    },
  },
  computed: {
    alreadyRead() {
      return Boolean(this.document?.read_at || this.document?.is_read || this.document?.read);
    },
    currentUserId() {
      const storeUser = this.user();
      if (storeUser?.id) return storeUser.id;
      if (storeUser?.user_id) return storeUser.user_id;
      try {
        const cached = JSON.parse(localStorage.getItem('user'));
        return cached?.id || cached?.user_id || null;
      } catch (_) {
        return null;
      }
    },
    currentUserEmail() {
      const storeUser = this.user();
      if (storeUser?.email) return storeUser.email;
      try {
        const cached = JSON.parse(localStorage.getItem('user'));
        return cached?.email || null;
      } catch (_) {
        return null;
      }
    },
    readOn() {
      // Prefer server timestamp; if absent but marked as read, show current time
      const ts = this.document?.read_at || (this.alreadyRead ? new Date().toISOString() : null);
      if (!ts) return '';
      try { return new Date(ts).toLocaleString(); } catch (e) { return ts; }
    },
    readOnRelative() {
      if (!this.alreadyRead) return '';
      const now = new Date();
      const then = new Date(this.document?.read_at || now);
      const diffMs = now - then;
      const sec = Math.floor(diffMs / 1000);
      const min = Math.floor(sec / 60);
      const hr = Math.floor(min / 60);
      const day = Math.floor(hr / 24);
      const month = Math.floor(day / 30);
      const year = Math.floor(day / 365);
      if (sec < 5) return 'just now';
      if (sec < 60) return `${sec} seconds ago`;
      if (min < 2) return 'a minute ago';
      if (min < 60) return `${min} minutes ago`;
      if (hr < 2) return 'an hour ago';
      if (hr < 24) return `${hr} hours ago`;
      if (day < 2) return 'yesterday';
      if (day < 30) return `${day} days ago`;
      if (month < 2) return 'a month ago';
      if (month < 12) return `${month} months ago`;
      if (year < 2) return 'a year ago';
      return `${year} years ago`;
    },
  },
};
</script>

<style scoped></style>
